import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/hooks/useAuth';
import { useAdminData } from '../hooks/useAdminData';
import { useMetrics } from '../hooks/useMetrics';
import OnboardingFunnel from '../components/OnboardingFunnel';
import Button from '@/common/ui/components/Button';

export default function AdminMetrics() {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { medications, patients, loading } = useAdminData();
  const metrics = useMetrics({ medications, patients });

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (error) {
      // Error will be handled by auth service
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-600">Loading metrics...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-title-lg font-inter text-text-primary">Metrics Dashboard</h1>
              <p className="text-supporting text-text-secondary font-inter">{user?.email}</p>
            </div>
            <div className="flex gap-4">
              <Button
                onClick={() => navigate('/admin/dashboard')}
                variant="ghost"
                size="md"
              >
                Back to Dashboard
              </Button>
              <Button
                onClick={handleSignOut}
                variant="ghost"
                size="md"
              >
                Sign Out
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Metrics Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Total Students */}
          <div className="bg-pure-white p-6 rounded-md shadow-card">
            <h3 className="text-section-header text-gray-900 font-inter mb-2">Total Students</h3>
            <p className="text-display text-trust-blue font-inter mb-2">{metrics.totalStudents}</p>
            <p className="text-supporting text-text-secondary">Registered patient accounts</p>
          </div>

          {/* Premium Subscribers */}
          <div className="bg-pure-white p-6 rounded-md shadow-card">
            <h3 className="text-section-header text-gray-900 font-inter mb-2">Premium Subscribers</h3>
            <p className="text-display text-energy-coral font-inter mb-2">{metrics.premiumUsers}</p>
            <p className="text-supporting text-text-secondary">Users with PRO tier subscription</p>
          </div>

          {/* Active Medications */}
          <div className="bg-pure-white p-6 rounded-md shadow-card">
            <h3 className="text-section-header text-gray-900 font-inter mb-2">Active Medications</h3>
            <p className="text-display text-wellness-mint font-inter mb-2">{metrics.activeMedications}</p>
            <p className="text-supporting text-text-secondary">Currently being tracked</p>
          </div>

          {/* Successful Pickups - Monthly */}
          <div className="bg-pure-white p-6 rounded-md shadow-card">
            <h3 className="text-section-header text-gray-900 font-inter mb-2">Pickups This Month</h3>
            <p className="text-display text-energy-coral font-inter mb-2">{metrics.successfulPickupsMonthly}</p>
            <p className="text-supporting text-text-secondary">Confirmed pickups in current month</p>
          </div>

          {/* Successful Pickups - All Time */}
          <div className="bg-pure-white p-6 rounded-md shadow-card">
            <h3 className="text-section-header text-gray-900 font-inter mb-2">Total Pickups</h3>
            <p className="text-display text-trust-blue font-inter mb-2">{metrics.successfulPickupsAllTime}</p>
            <p className="text-supporting text-text-secondary">All-time confirmed pickups</p>
          </div>

          {/* Average Time to Pickup */}
          <div className="bg-pure-white p-6 rounded-md shadow-card">
            <h3 className="text-section-header text-gray-900 font-inter mb-2">Avg Time to Pickup</h3>
            <p className="text-display text-wellness-mint font-inter mb-2">
              {metrics.averageTimeToPickup > 0 ? `${metrics.averageTimeToPickup} days` : 'N/A'}
            </p>
            <p className="text-supporting text-text-secondary">From notification to pickup</p>
          </div>

          {/* Pharmacy Success Rate */}
          <div className="bg-pure-white p-6 rounded-md shadow-card">
            <h3 className="text-section-header text-gray-900 font-inter mb-2">Pharmacy Success Rate</h3>
            <p className="text-display text-energy-coral font-inter mb-2">
              {metrics.pharmacySuccessRate}%
            </p>
            <p className="text-supporting text-text-secondary">Medications in stock vs out of stock</p>
          </div>
        </div>

        {/* Onboarding Funnel Section */}
        <div className="mt-8">
          <h2 className="text-section-header text-gray-900 font-inter mb-4">Onboarding Performance</h2>
          <OnboardingFunnel />
        </div>

        {/* Additional Info */}
        <div className="mt-8 bg-blue-50 border border-blue-200 rounded-md p-4">
          <h4 className="text-sm font-medium text-blue-900 mb-2">About These Metrics</h4>
          <ul className="text-sm text-blue-700 space-y-1">
            <li><strong>Total Students:</strong> Number of users with patient role</li>
            <li><strong>Premium Subscribers:</strong> Number of users with active PRO tier subscription</li>
            <li><strong>Active Medications:</strong> Medications currently being tracked (not inactive)</li>
            <li><strong>Pickups This Month:</strong> Medications picked up in the current calendar month</li>
            <li><strong>Total Pickups:</strong> All medications that have been picked up at least once</li>
            <li><strong>Avg Time to Pickup:</strong> Average number of days between sending notification and patient confirming pickup</li>
            <li><strong>Pharmacy Success Rate:</strong> Percentage of pharmacy calls where medication was in stock (vs out of stock)</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
